<template>
  <div class="article-container">
    <div class="article-main">
      <div class="article-header">
        <h1 class="article-title">{{ article.title }}</h1>
        <div class="article-meta">
          <div class="meta-left">
            <div class="author-info">
              <el-avatar :size="40" :src="article.authorIcon" icon="el-icon-user" style="cursor: pointer;" @click="goToUserCenter"></el-avatar>
              <div class="author-detail">
                <span class="author-name" style="cursor: pointer;" @click="goToUserCenter">{{ article.authorName }}</span>
                <span class="publish-time">
                  <i class="el-icon-time"></i> {{ formatDate(article.createTime) }}
                </span>
              </div>
            </div>
          </div>
          <div class="meta-right">
            <div class="article-stats">
              <span class="stat-item">
                <i class="el-icon-view"></i> {{ article.viewCount || 0 }}
              </span>
              <span class="stat-item">
                <i class="el-icon-chat-dot-round"></i> {{ article.commentCount || 0 }}
              </span>
              <span class="stat-item" @click="vote(1)" :class="{ active: userVote === 1 }">
                <i class="el-icon-caret-top"></i> {{ article.upvoteCount || 0 }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="article-content markdown-body" v-html="renderedContent"></div>

      <div class="article-tags" v-if="article.tags && article.tags.length > 0">
        <el-tag
          v-for="tag in article.tags"
          :key="tag.id"
          size="small"
          effect="plain"
          class="tag-item"
        >
          {{ tag.tagName }}
        </el-tag>
      </div>

      <div class="article-actions">
        <el-button 
          type="danger" 
          round 
          :class="{ active: userVote === 1 }"
          @click="vote(1)"
        >
          <i class="el-icon-caret-top"></i> 点赞 {{ article.upvoteCount || 0 }}
        </el-button>
        <el-button round @click="scrollToComment">
          <i class="el-icon-chat-dot-round"></i> 评论 {{ article.commentCount || 0 }}
        </el-button>
        <el-button round @click="handleShare">
          <i class="el-icon-share"></i> 分享
        </el-button>
      </div>

      <div class="comment-section" ref="commentSection">
        <div class="comment-header">
          <h3>评论</h3>
          <span class="comment-count">({{ article.commentCount || 0 }})</span>
        </div>
        <LocalComment :sourceId="articleId" @commentAdded="onCommentAdded"></LocalComment>
      </div>
    </div>

    <div class="article-sidebar">
      <div class="category-card" v-if="article.category">
        <div class="category-header">
          <i class="el-icon-folder"></i>
          <span>所属分类</span>
        </div>
        <div class="category-content" @click="goToCategory">
          <span class="category-name">{{ article.category.categoryName }}</span>
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>

      <div class="author-card">
        <div class="author-header">
          <i class="el-icon-user"></i>
          <span>关于作者</span>
        </div>
        <div class="author-content" @click="goToUserCenter">
          <el-avatar :size="60" :src="article.authorIcon" icon="el-icon-user" style="cursor: pointer;"></el-avatar>
          <div class="author-detail">
            <div class="author-name" style="cursor: pointer;">{{ article.authorName }}</div>
            <div class="author-desc">点击查看作者主页</div>
          </div>
        </div>
        <el-button type="primary" plain size="small" round @click="followAuthor">
          {{ isFollowing ? '已关注' : '+ 关注' }}
        </el-button>
      </div>

      <div class="related-articles">
        <div class="sidebar-header">
          <i class="el-icon-document"></i>
          <span>相关文章</span>
        </div>
        <div class="related-list">
          <div
            v-for="item in relatedList"
            :key="item.id"
            class="related-item"
            @click="goToArticle(item.id)"
          >
            <span class="related-title">{{ item.title }}</span>
          </div>
          <el-empty v-if="relatedList.length === 0" description="暂无相关文章" :image-size="60"></el-empty>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LocalComment from '../components/LocalComment.vue';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  breaks: true
});

export default {
  name: 'Content',
  components: { LocalComment },
  data() {
    return {
      article: {
        id: null,
        title: '',
        content: '',
        authorId: '',
        authorName: '',
        authorIcon: '',
        createTime: '',
        viewCount: 0,
        commentCount: 0,
        upvoteCount: 0,
        category: null,
        tags: []
      },
      renderedContent: '',
      userVote: null,
      isFollowing: false,
      relatedList: [],
      articleId: this.$route.params.id
    };
  },
  created() {
    this.getArticleDetail();
    this.getRelatedArticles();
    this.getCommentCount();
  },
  methods: {
    async getArticleDetail() {
      try {
        const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
        const response = await this.$axios({
          method: 'post',
          url: '/doc/getDoc',
          data: {
            docDigest: this.articleId,
            userId: loginResult.userId || '',
            token: loginResult.token || '',
            ip: '',
            source: 'web',
            userName: loginResult.userName || ''
          }
        });
        
        if (response.code === '200' || response.code === 200) {
          const data = response.data;
          console.log('接口返回数据:', data);
          Object.assign(this.article, {
            id: data.docDigest,
            title: data.title || '',
            content: data.content || '',
            authorId: data.createUserId || '',
            authorName: data.createUserName || '未知作者',
            authorIcon: data.createIcon || '',
            createTime: data.createTime,
            viewCount: data.viewCount || 0,
            commentCount: data.commentCount || 0,
            upvoteCount: data.praiseCount || 0,
            category: {
              id: data.categoryId,
              categoryName: data.categoryName
            }
          });
          const rawContent = this.article.content || '';
          console.log('原始 content:', rawContent);
          const cleanedContent = rawContent.replace(/>\s+</g, '><');
          console.log('清理后的 content:', cleanedContent);
          this.renderedContent = md.render(cleanedContent);
          console.log('renderedContent:', this.renderedContent);
          
          // 检查是否已关注作者
          if (this.article.authorId && loginResult.userId) {
            this.checkIsFollowing(loginResult.userId, this.article.authorId);
          }
        } else {
          this.$message.error(response.msg || '获取文章失败');
        }
      } catch (error) {
        console.error('获取文章详情失败:', error);
        this.$message.error('获取文章失败，请稍后重试');
      }
    },
    async getRelatedArticles() {
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/doc/related',
          data: {
            docId: this.articleId,
            limit: 5
          }
        });
        
        if (response.code === '200') {
          this.relatedList = response.data || [];
        }
      } catch (error) {
        console.error('获取相关文章失败:', error);
      }
    },
    async vote(direction) {
      const loginResult = localStorage.getItem('loginResult');
      if (!loginResult) {
        this.$message.warning('请先登录');
        this.$router.push({ name: 'Login', query: { redirect: this.$route.path } });
        return;
      }

      try {
        const response = await this.$axios({
          method: 'post',
          url: '/doc/praise',
          data: {
            docDigest: this.articleId,
            isLike: direction === 1 ? 1 : 0,
            token: JSON.parse(loginResult).token || '',
            userId: JSON.parse(loginResult).userId || '',
            userName: JSON.parse(loginResult).userName || '',
            ip: '',
            source: 'web'
          }
        });

        if (response.code === '200' || response.code === 200 || response.code === 1000) {
          this.userVote = this.userVote === 1 ? null : 1;
          this.article.upvoteCount = this.userVote === 1 
            ? (this.article.upvoteCount || 0) + 1 
            : (this.article.upvoteCount || 0) - 1;
          this.$message.success(this.userVote === 1 ? '点赞成功' : '已取消点赞');
        } else {
          this.$message.warning(response.msg || '操作失败');
        }
      } catch (error) {
        console.error('投票失败:', error);
        this.$message.error('操作失败，请稍后重试');
      }
    },
    goToUserCenter() {
      if (this.article.authorId) {
        this.$router.push({ name: 'OtherUserCenter', params: { userId: this.article.authorId } });
      } else {
        this.$message.warning('作者信息不可用');
      }
    },
    scrollToComment() {
      this.$nextTick(() => {
        this.$refs.commentSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    onCommentAdded() {
      this.article.commentCount = (this.article.commentCount || 0) + 1;
      this.getCommentCount();
    },
    async getCommentCount() {
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/comment/count',
          data: {
            sourceId: this.articleId
          }
        });
        if (response.code === '200' || response.code === 200) {
          this.article.commentCount = response.data || 0;
        }
      } catch (error) {
        console.error('获取评论数失败:', error);
      }
    },
    handleShare() {
      const url = window.location.href;
      
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(() => {
          this.$message.success('链接已复制到剪贴板');
        }).catch(() => {
          this.$message.warning('复制失败，请手动复制');
        });
      } else {
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.$message.success('链接已复制到剪贴板');
      }
    },
    async checkIsFollowing(userId, followUserId) {
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/userfollow/isFollowed',
          data: {
            userId: userId,
            followUserId: followUserId
          }
        });
        
        if (response.code === '200' || response.code === 200) {
          this.isFollowing = response.data || false;
        }
      } catch (error) {
        console.error('检查关注状态失败:', error);
      }
    },
    followAuthor() {
      const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
      if (!loginResult.userId) {
        this.$message.warning('请先登录');
        this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath } });
        return;
      }
      
      if (!this.article.authorId) {
        this.$message.warning('作者信息不可用');
        return;
      }
      
      const url = this.isFollowing ? '/userfollow/unfollow' : '/userfollow/follow';
      this.$axios({
        method: 'post',
        url: url,
        data: {
          userId: loginResult.userId,
          followUserId: this.article.authorId
        }
      }).then(response => {
        if (response.code === '200' || response.code === 200) {
          this.isFollowing = !this.isFollowing;
          this.$message.success(this.isFollowing ? '关注成功' : '取消关注成功');
        } else {
          this.$message.error(response.msg || '操作失败');
        }
      }).catch(error => {
        console.error('关注操作失败:', error);
        this.$message.error('操作失败');
      });
    },
    goToCategory() {
      if (this.article.category?.id) {
        this.$router.push({ path: '/community', query: { categoryId: this.article.category.id } });
      }
    },
    goToArticle(id) {
      this.$router.push({ name: 'Content', params: { id } });
      window.scrollTo(0, 0);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diff = now - date;
      
      const minute = 60 * 1000;
      const hour = 60 * minute;
      const day = 24 * hour;
      const month = 30 * day;
      
      if (diff < minute) {
        return '刚刚';
      } else if (diff < hour) {
        return Math.floor(diff / minute) + '分钟前';
      } else if (diff < day) {
        return Math.floor(diff / hour) + '小时前';
      } else if (diff < month) {
        return Math.floor(diff / day) + '天前';
      } else {
        return date.toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    }
  },
  watch: {
    '$route.params.id'(newId) {
      this.articleId = newId;
      this.getArticleDetail();
      this.getRelatedArticles();
      window.scrollTo(0, 0);
    }
  }
};
</script>

<style lang="less" scoped>
.article-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  margin-top: 64px;
  display: flex;
  gap: 24px;
  min-height: calc(100vh - 64px);
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.article-main {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
}

.article-header {
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 24px;
  margin-bottom: 24px;

  .article-title {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 20px 0;
    line-height: 1.4;
  }

  .article-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .meta-left {
      .author-info {
        display: flex;
        align-items: center;
        gap: 12px;

        .author-detail {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .author-name {
            font-size: 15px;
            font-weight: 600;
            color: #333;
          }

          .publish-time {
            font-size: 13px;
            color: #999;
            display: flex;
            align-items: center;
            gap: 4px;
          }
        }
      }
    }

    .meta-right {
      .article-stats {
        display: flex;
        gap: 20px;

        .stat-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: #666;
          cursor: pointer;
          transition: all 0.3s;
          padding: 6px 12px;
          border-radius: 8px;
          background: #f5f5f5;

          &:hover {
            color: #667eea;
            background: rgba(102, 126, 234, 0.1);
          }

          &.active {
            color: #ff4757;
            background: rgba(255, 71, 87, 0.1);
          }

          i {
            font-size: 16px;
          }
        }
      }
    }
  }
}

.article-content {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  padding: 20px 0;
  background: #fff;

  ::v-deep {
    h1, h2, h3, h4, h5, h6 {
      margin: 24px 0 16px 0;
      font-weight: 600;
      color: #1a1a1a;
    }

    h1 { font-size: 24px; }
    h2 { font-size: 20px; }
    h3 { font-size: 18px; }

    p {
      margin: 16px 0;
    }

    code {
      background: #f5f5f5;
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'Fira Code', monospace;
      font-size: 14px;
    }

    pre {
      background: #282c34;
      color: #abb2bf;
      padding: 16px;
      border-radius: 8px;
      overflow-x: auto;
      margin: 16px 0;

      code {
        background: transparent;
        padding: 0;
        color: inherit;
      }
    }

    blockquote {
      border-left: 4px solid #667eea;
      margin: 16px 0;
      padding: 12px 16px;
      background: #f8f9ff;
      color: #666;
      border-radius: 0 8px 8px 0;
    }

    ul, ol {
      padding-left: 24px;
      margin: 16px 0;
    }

    li {
      margin: 8px 0;
    }

    img {
      max-width: 100%;
      border-radius: 8px;
      margin: 16px 0;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 16px 0;

      th, td {
        border: 1px solid #e0e0e0;
        padding: 12px;
        text-align: left;
      }

      th {
        background: #f5f5f5;
        font-weight: 600;
      }
    }

    a {
      color: #667eea;
      text-decoration: none;
      border-bottom: 1px solid transparent;
      transition: all 0.3s;

      &:hover {
        border-bottom-color: #667eea;
      }
    }
  }
}

.article-tags {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding: 20px 0;
  border-top: 1px solid #f0f0f0;
  margin-top: 20px;

  .tag-item {
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
    }
  }
}

.article-actions {
  display: flex;
  gap: 12px;
  padding: 24px 0;
  border-top: 2px solid #f0f0f0;
  margin-top: 20px;

  .el-button {
    flex: 1;
    max-width: 150px;
    height: 44px;
    font-size: 15px;
    font-weight: 500;
    transition: all 0.3s;

    &.active {
      background: linear-gradient(135deg, #ff6b6b 0%, #ff4757 100%);
      border-color: transparent;
      color: #fff;
      box-shadow: 0 4px 16px rgba(255, 71, 87, 0.3);
    }

    &:hover {
      transform: translateY(-2px);
    }
  }
}

.comment-section {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 2px solid #f0f0f0;

  .comment-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 24px;

    h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #333;
    }

    .comment-count {
      font-size: 14px;
      color: #999;
    }
  }
}

.article-sidebar {
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;

  .category-card,
  .author-card,
  .related-articles {
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  }

  .category-header,
  .author-header,
  .sidebar-header {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    padding-bottom: 14px;
    border-bottom: 2px solid #f0f0f0;
    margin-bottom: 16px;

    i {
      color: #667eea;
      font-size: 18px;
    }
  }

  .category-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background: #f0f0f0;

      .category-name {
        color: #667eea;
      }
    }

    .category-name {
      font-size: 14px;
      color: #333;
      font-weight: 500;
      transition: color 0.3s;
    }

    i {
      color: #999;
    }
  }

  .author-content {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 0;

    .author-detail {
      flex: 1;

      .author-name {
        font-size: 15px;
        font-weight: 600;
        color: #333;
        margin-bottom: 6px;
      }

      .author-desc {
        font-size: 13px;
        color: #999;
      }
    }
  }

  .related-list {
    .related-item {
      padding: 12px;
      margin-bottom: 8px;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        background: #f8f9fa;

        .related-title {
          color: #667eea;
        }
      }

      .related-title {
        font-size: 14px;
        color: #333;
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        transition: color 0.3s;
      }
    }
  }
}

@media (max-width: 1024px) {
  .article-container {
    flex-direction: column;
  }

  .article-sidebar {
    width: 100%;
  }
}
</style>
